import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { renderTextWithLinks } from '@/utils/marketing-helpers';
import { useTranslation } from 'react-i18next';

interface AnswerObject {
    intro?: string;
    list?: string[];
    important?: string;
    explanation?: string;
    requirements?: { label: string; description: string }[];
    residential?: { title: string; points: string[] };
    commercial?: { title: string; points: string[] };
}

interface FaqProps {
    faqs: Array<{
        question: string;
        answer: string | AnswerObject;
    }>;
    title: string; 
}

export default function Faq({ 
    faqs,
    title
}: FaqProps) {
    const { t } = useTranslation();

    const renderAnswer = (answer: string | AnswerObject) => {
        if (typeof answer === 'string') {
            return <p className="text-lg text-paragraph">{renderTextWithLinks(answer)}</p>;
        }

        const obj = answer as AnswerObject;

        return (
            <div>
                {obj.intro && <p className="mb-4 text-lg text-paragraph">{renderTextWithLinks(obj.intro)}</p>}

                {obj.list && (
                    <ul className="mb-4 ml-4 space-y-2 text-lg text-paragraph">
                        {obj.list.map((item, index) => (
                            <li key={index}>• {item}</li>
                        ))}
                    </ul>
                )}

                {obj.requirements && (
                    <ul className="ml-4 space-y-2 text-lg text-paragraph">
                        {obj.requirements.map((req, index) => (
                            <li key={index}>
                                • <strong className="text-hs-blue dark:text-hs-green-light">{req.label}:</strong> {req.description}
                            </li>
                        ))}
                    </ul>
                )}

                {obj.residential && obj.commercial && (
                    <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                        <div className="rounded-lg border-l-4 border-hs-blue bg-blue-50 p-6 dark:bg-blue-900/20">
                            <h4 className="mb-4 text-lg font-semibold text-hs-blue dark:text-hs-blue-light">{obj.residential.title}</h4>
                            <ul className="space-y-2 text-lg leading-7 text-paragraph">
                                {obj.residential.points.map((point, index) => (
                                    <li key={index}>• {point}</li>
                                ))}
                            </ul>
                        </div>
                        <div className="rounded-lg border-l-4 border-hs-green bg-green-50 p-6 dark:bg-green-900/20">
                            <h4 className="mb-4 text-lg font-semibold text-hs-green dark:text-hs-green-light">{obj.commercial.title}</h4>
                            <ul className="space-y-2 text-lg leading-7 text-paragraph">
                                {obj.commercial.points.map((point, index) => (
                                    <li key={index}>• {point}</li>
                                ))}
                            </ul>
                        </div>
                    </div>
                )}

                {obj.important && (
                    <div className="rounded-lg bg-blue-50 p-4 dark:bg-gray-700/30">
                        <p className="text-base font-medium text-hs-blue dark:text-hs-green-light">
                            {obj.important.includes('Hintergrund:') ? (
                                <>
                                    <strong>
                                        {t('common.background')}: {obj.important.replace('Hintergrund: ', '').replace('.', '')}
                                    </strong>
                                </>
                            ) : (
                                <>
                                    <strong>{t('common.important')}:</strong> {obj.important}
                                </>
                            )}
                        </p>
                        {obj.explanation && <p className="text-lg text-hs-blue dark:text-hs-green-light">{obj.explanation}</p>}
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className="bg-linear-to-b from-gray-50 to-white dark:bg-linear-to-b dark:from-gray-950 dark:to-gray-900" id="faq">
            <div className="mx-auto max-w-7xl px-6 py-12 lg:px-8 lg:py-24">
                <h2 className="text-center text-4xl font-semibold tracking-tight text-headline sm:text-5xl">{title}</h2>
                <Accordion type="multiple" className="mt-10 space-y-6 divide-y divide-gray-900/10 dark:divide-white/10">
                    {faqs.map((faq, index) => (
                        <AccordionItem key={index} value={`item-${index}`} className="pt-6">
                            <AccordionTrigger className="group flex w-full items-start justify-between text-left text-primary transition-colors hover:text-hs-blue hover:no-underline dark:hover:text-hs-green-light [&>svg]:text-hs-blue [&>svg]:dark:text-hs-green-light">
                                <span className="text-lg font-semibold text-headline hover:text-hs-blue dark:hover:text-hs-green-light">
                                    {faq.question}
                                </span>
                            </AccordionTrigger>
                            <AccordionContent className="mt-2 pr-12">{renderAnswer(faq.answer)}</AccordionContent>
                        </AccordionItem>
                    ))}
                </Accordion>
            </div>
        </div>
    );
}
